import { compact } from '@leland-dev/leland-ui-library';
import {
  type GetStaticPaths,
  type GetStaticProps,
  type GetStaticPropsResult,
} from 'next';
import React, { type FC } from 'react';

import {
  type ClassPageStaticProps,
  getClassPageStaticData,
} from '../../components/classes/ClassPage';
import EventPage from '../../components/event/EventPage';
import {
  PublishedEventPageGetStaticClassPathsDocument,
  type PublishedEventPageGetStaticClassPathsQuery,
  type PublishedEventPageGetStaticClassPathsQueryVariables,
} from '../../components/event/__generated-gql-types__/[eventSlug].generated';
import Page from '../../components/page/Page';
import PageContent from '../../components/page/PageContent';
import { initializeApollo } from '../../lib/apolloClient';

const PublishedEventPage: FC<ClassPageStaticProps> = ({
  staticClassData,
  pageContentProps,
  eventSlug,
}) => {
  const title = staticClassData.name ?? 'Event';
  return (
    <Page title={title}>
      <PageContent initialData={pageContentProps}>
        <EventPage
          staticClassData={staticClassData}
          pageContentProps={pageContentProps}
          eventSlug={eventSlug}
        />
      </PageContent>
    </Page>
  );
};

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type PathParams = {
  eventSlug?: string;
};
export const getStaticPaths: GetStaticPaths<PathParams> = async () => {
  const apolloClient = initializeApollo();

  const { data } = await apolloClient.query<
    Possible<PublishedEventPageGetStaticClassPathsQuery>,
    PublishedEventPageGetStaticClassPathsQueryVariables
  >({ query: PublishedEventPageGetStaticClassPathsDocument });

  return {
    paths: (data?.searchClasses.results ?? [])
      .map(({ slug }) => (slug ? { params: { eventSlug: slug } } : null))
      .filter(compact),
    fallback: 'blocking',
  };
};

const REVALIDATE_TIME_IN_SECONDS = 3600; // 1 hour
const NOT_FOUND_PROPS: GetStaticPropsResult<ClassPageStaticProps> = {
  notFound: true,
  revalidate: REVALIDATE_TIME_IN_SECONDS,
};

export const getStaticProps: GetStaticProps<
  ClassPageStaticProps,
  PathParams
> = async ({ params: { eventSlug } = {} }) => {
  if (!eventSlug) {
    return NOT_FOUND_PROPS;
  }

  const apolloClient = initializeApollo();
  const { props: propsToUse, errors } = await getClassPageStaticData(
    apolloClient,
    eventSlug,
  );

  if (!propsToUse?.staticClassData) {
    if (!errors || errors?.some((e) => e.extensions?.statusCode === 404)) {
      return NOT_FOUND_PROPS;
    }

    // If not 404, throw to explicitly treat as 5xx
    console.warn(errors);
    throw new Error('Failed to load event data');
  }

  return {
    props: {
      ...propsToUse,
      eventSlug,
    },
    revalidate: REVALIDATE_TIME_IN_SECONDS,
  };
};

export default PublishedEventPage;
